import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bulkUploadContacts, getBulkUploadTemplateUrl } from '../utils/api';

const CAT_LABELS = {
  lp_cogp: 'LP / Co-GP', lender: 'Lender', broker: 'Broker',
  partner_service: 'Partner / Service', seller_operator: 'Seller / Operator',
};

export default function BulkUploadPage() {
  const [dragging,  setDragging]  = useState(false);
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results,   setResults]   = useState(null);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      setError('Please upload a CSV file.');
      return;
    }
    setFile(f);
    setResults(null);
    setError(null);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await bulkUploadContacts(file);
      setResults(res);
      setFile(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResults(null);
    setError(null);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Bulk upload contacts</h1>
          <p className="page-subtitle">Import multiple contacts at once from a CSV file</p>
        </div>
        <Link to="/contacts" className="btn">← Back to contacts</Link>
      </div>

      {/* Instructions */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>How it works</div>
            <ol style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', paddingLeft: 18, lineHeight: 2 }}>
              <li>Download the CSV template below</li>
              <li>Fill in your contacts — one per row</li>
              <li>Upload the completed file</li>
              <li>Review the results — imported, skipped, and any errors</li>
            </ol>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 8 }}>
              You will be set as the owner of all imported contacts.
              Contacts with duplicate emails will be flagged for review.
            </div>
          </div>
          <a
            href={getBulkUploadTemplateUrl()}
            download="contacts_template.csv"
            className="btn btn-primary"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Download CSV template
          </a>
        </div>

        {/* Category reference */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--color-border-light)', paddingTop: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.07em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Valid category values
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(CAT_LABELS).map(([val, label]) => (
              <span key={val} style={{ padding: '2px 10px', borderRadius: 10, fontSize: '0.78rem',
                background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)' }}>
                {val} <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-ui)' }}>= {label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Drop zone */}
      {!results && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !file && inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--color-accent)' : file ? 'var(--color-accent)' : 'var(--color-border)'}`,
            borderRadius: 10,
            padding: '40px 24px',
            textAlign: 'center',
            background: dragging ? 'var(--color-accent-light)' : file ? 'var(--color-accent-light)' : 'transparent',
            cursor: file ? 'default' : 'pointer',
            transition: 'all 0.15s',
            marginBottom: 20,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])}
          />

          {file ? (
            <div>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📄</div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{file.name}</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: 16 }}>
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="btn btn-primary"
                >
                  {uploading ? 'Uploading...' : 'Upload and import'}
                </button>
                <button onClick={reset} className="btn">Choose different file</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📂</div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                Drop your CSV file here
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: 12 }}>
                or click to browse
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                CSV files only · Max 5MB
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          fontSize: '0.85rem', color: 'var(--color-danger)' }}>
          {error}
          <button onClick={reset} style={{ marginLeft: 12, background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--color-danger)', textDecoration: 'underline',
            fontSize: '0.82rem', fontFamily: 'var(--font-ui)' }}>
            Try again
          </button>
        </div>
      )}

      {/* Results */}
      {results && (
        <div>
          {/* Summary bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
            <div className="metric-card">
              <div className="metric-label">Imported</div>
              <div className="metric-value" style={{ color: 'var(--color-success)' }}>
                {results.summary.imported}
              </div>
              <div className="metric-sub">contacts added</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Skipped</div>
              <div className="metric-value" style={{ color: results.summary.skipped > 0 ? 'var(--color-warning)' : undefined }}>
                {results.summary.skipped}
              </div>
              <div className="metric-sub">duplicates</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Errors</div>
              <div className="metric-value" style={{ color: results.summary.errors > 0 ? 'var(--color-danger)' : undefined }}>
                {results.summary.errors}
              </div>
              <div className="metric-sub">rows failed</div>
            </div>
          </div>

          {/* Imported */}
          {results.imported.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '10px 16px', fontWeight: 500, fontSize: '0.85rem',
                borderBottom: '1px solid var(--color-border-light)',
                display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--color-success)' }}>✓</span>
                {results.imported.length} contact{results.imported.length !== 1 ? 's' : ''} imported successfully
              </div>
              {results.imported.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '8px 16px', borderBottom: i < results.imported.length - 1 ? '1px solid var(--color-border-light)' : 'none',
                  fontSize: '0.85rem' }}>
                  <span>{r.name}</span>
                  <span style={{ color: 'var(--color-text-muted)' }}>{r.category}</span>
                </div>
              ))}
            </div>
          )}

          {/* Skipped — duplicates */}
          {results.skipped.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '10px 16px', fontWeight: 500, fontSize: '0.85rem',
                borderBottom: '1px solid var(--color-border-light)',
                display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--color-warning)' }}>⚠</span>
                {results.skipped.length} row{results.skipped.length !== 1 ? 's' : ''} skipped — duplicates
              </div>
              {results.skipped.map((r, i) => (
                <div key={i} style={{ padding: '10px 16px',
                  borderBottom: i < results.skipped.length - 1 ? '1px solid var(--color-border-light)' : 'none' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem', marginBottom: 2 }}>
                    Row {r.row} — {r.data}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    {r.reason}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Errors */}
          {results.errors.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '10px 16px', fontWeight: 500, fontSize: '0.85rem',
                borderBottom: '1px solid var(--color-border-light)',
                display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--color-danger)' }}>✕</span>
                {results.errors.length} row{results.errors.length !== 1 ? 's' : ''} failed — fix and re-upload
              </div>
              {results.errors.map((r, i) => (
                <div key={i} style={{ padding: '10px 16px',
                  borderBottom: i < results.errors.length - 1 ? '1px solid var(--color-border-light)' : 'none' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem', marginBottom: 2 }}>
                    Row {r.row} — {r.data}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-danger)' }}>
                    {r.reasons.join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={reset} className="btn btn-primary">Upload another file</button>
            <Link to="/contacts" className="btn">View all contacts</Link>
          </div>
        </div>
      )}
    </div>
  );
}
